/* Harper & Stone — collection filtering.
 *
 * The form is a real GET form: without JS it submits, the page reloads, and
 * every filter works. This adds two things on top — results that swap in
 * place, and a submit button that reports the live count before the shopper
 * commits to it.
 *
 * That live count is the whole point of the drawer. "Show 12" tells you
 * whether a filter is worth applying; "Apply" does not.
 */

const DEBOUNCE = 350;

class HsCollection extends HTMLElement {
  #controller = null;
  #timer = null;

  connectedCallback() {
    this.drawer = this.querySelector('[data-filter-drawer]');
    this.form = this.querySelector('[data-filter-form]');
    this.results = this.querySelector('[data-collection-results]');
    this.count = this.querySelector('[data-collection-count]');
    this.submit = this.querySelector('[data-filter-submit]');
    this.badge = this.querySelector('.ctoolbar__badge');

    for (const open of this.querySelectorAll('[data-filter-open]')) {
      open.addEventListener('click', () => this.#open());
    }
    for (const close of this.querySelectorAll('[data-filter-close]')) {
      close.addEventListener('click', () => this.drawer?.close());
    }

    this.drawer?.addEventListener('close', () => {
      document.body.style.removeProperty('overflow');
      this.querySelector('[data-filter-open]')?.focus();
    });

    // A click on the backdrop closes. <dialog> reports those as clicks on
    // itself, so compare against the dialog's own box.
    this.drawer?.addEventListener('click', (e) => {
      if (e.target !== this.drawer) return;
      const r = this.drawer.getBoundingClientRect();
      const inside =
        e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) this.drawer.close();
    });

    // Changing a facet previews the count; it does not navigate. The shopper
    // decides when to commit, which is what the pinned button is for.
    this.form?.addEventListener('change', () => this.#preview());
    this.form?.addEventListener('input', (e) => {
      if (e.target.type === 'number') this.#preview();
    });

    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.#apply(this.#url());
      this.drawer?.close();
    });

    this.querySelector('[data-filter-clear]')?.addEventListener('click', () => {
      for (const el of this.form.elements) {
        if (el.type === 'checkbox') el.checked = false;
        if (el.type === 'number') el.value = '';
      }
      this.#preview();
    });

    // Chips and pagination swap in place too, so the drawer's scroll position
    // and the page's feel stay consistent with filtering.
    this.addEventListener('click', (e) => {
      const link = e.target.closest('.cchip, .cpager__item[href]');
      if (!link || !link.href) return;
      e.preventDefault();
      this.#apply(new URL(link.href));
    });

    window.addEventListener('popstate', () => this.#apply(new URL(location.href), false));
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#timer);
  }

  #open() {
    this.drawer?.showModal();
    document.body.style.overflow = 'hidden';
    this.#accordion();
  }

  /* One group open at a time.
   *
   * Every group rendering open at once made the panel a long scroll on which
   * the sort list alone filled the screen, so the filters themselves were
   * below the fold — the panel opened on the one thing the toolbar already
   * offers and hid the two things it does not.
   *
   * The group carrying an active filter wins, because that is the one the
   * shopper is working in. Otherwise the first real filter opens and sort
   * stays closed.
   *
   * `toggle` fires before the open state is read, so the handler closes the
   * others on the way in rather than fighting the browser's own toggle. */
  #accordion() {
    const groups = [...this.drawer.querySelectorAll('.fdrawer__group')];
    if (!groups.length) return;

    if (!this.accordionBound) {
      for (const g of groups) {
        g.addEventListener('toggle', () => {
          if (!g.open) return;
          for (const other of groups) {
            if (other === g || !other.open) continue;
            /* Ask the motion layer to close it smoothly; if it is not there
             * (or motion is reduced) the event comes back unhandled and the
             * group closes the way it always did. */
            const handled = !other.dispatchEvent(
              new CustomEvent('hs:details-close', { bubbles: true, cancelable: true }),
            );
            if (!handled) other.open = false;
          }
        });
      }
      this.accordionBound = true;
    }

    const active =
      groups.find((g) => g.querySelector('input:checked:not([name="sort_by"])')) ||
      groups.find((g) => !g.querySelector('input[name="sort_by"]')) ||
      groups[0];

    for (const g of groups) g.open = g === active;
  }

  /* The form's own state as a URL, dropping empty values so the address bar
   * stays readable and shareable. */
  #url() {
    const url = new URL(location.href);
    url.search = '';
    const data = new FormData(this.form);
    for (const [k, v] of data.entries()) {
      if (v !== '' && v != null) url.searchParams.append(k, v);
    }
    return url;
  }

  #preview() {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(async () => {
      const url = this.#url();
      const html = await this.#fetch(url);
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const label = doc.querySelector('[data-filter-submit]')?.textContent?.trim();
      if (label && this.submit) this.submit.textContent = label;
    }, DEBOUNCE);
  }

  async #apply(url, push = true) {
    this.results?.setAttribute('aria-busy', 'true');
    const html = await this.#fetch(url);
    this.results?.removeAttribute('aria-busy');
    if (!html) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('[data-collection-results]');
    /* The grid used to be replaced between one frame and the next: 24 cards
     * gone, 9 others in their place, no indication that anything had been
     * asked for or answered. The shared fade gives the change a beginning
     * and an end. The network wait has already happened by this point, so
     * the fade costs the shopper 160ms, not 160ms on top of the fetch. */
    if (next && this.results) {
      const swap = window.HS?.swap ?? ((_, change) => change());
      await swap(this.results, () => {
        this.results.innerHTML = next.innerHTML;
      });
    }

    const nextCount = doc.querySelector('[data-collection-count]');
    if (nextCount && this.count) this.count.textContent = nextCount.textContent;

    const nextChips = doc.querySelector('.cchips');
    const chips = this.querySelector('.cchips');
    if (nextChips && chips) chips.innerHTML = nextChips.innerHTML;

    const nextBadge = doc.querySelector('.ctoolbar__badge');
    if (nextBadge && this.badge) {
      this.badge.textContent = nextBadge.textContent;
      this.badge.hidden = nextBadge.hidden;
    }

    // Keep the drawer's own controls in step with what was applied, so
    // reopening it shows the truth rather than the previous state.
    const nextBody = doc.querySelector('.fdrawer__body');
    const body = this.querySelector('.fdrawer__body');
    if (nextBody && body) {
      const openGroups = [...body.querySelectorAll('details')].map((d) => d.open);
      body.innerHTML = nextBody.innerHTML;
      [...body.querySelectorAll('details')].forEach((d, i) => {
        if (openGroups[i] != null) d.open = openGroups[i];
      });
    }

    if (push) history.pushState({}, '', url.toString());
    this.results?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async #fetch(url) {
    this.#controller?.abort();
    this.#controller = new AbortController();
    try {
      const res = await fetch(url.toString(), { signal: this.#controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.text();
    } catch (e) {
      if (e.name === 'AbortError') return null;
      // A failed filter must not strand the shopper on a stale grid, so fall
      // back to a real navigation.
      location.assign(url.toString());
      return null;
    }
  }
}

if (!customElements.get('hs-collection')) customElements.define('hs-collection', HsCollection);

/* Back to top. Appears once the shopper is deep enough in a grid that
 * scrolling back is a chore. */
class HsToTop extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    this.onScroll = () => {
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        this.dataset.visible = String(window.scrollY > window.innerHeight * 2);
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    cancelAnimationFrame(this.raf);
  }
}

if (!customElements.get('hs-to-top')) customElements.define('hs-to-top', HsToTop);
